import React, { useEffect, useState, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Briefcase, CreditCard, Wallet, Settings as SettingsIcon, LogOut, UsersRound, FileText, CalendarClock, FolderKanban, UserCog, ShieldCheck, ScrollText, GitBranch, Banknote, Wrench, Star, GripVertical, Activity, Layers, Copy, ClipboardCheck, Archive, Handshake, MessageSquare, Bell, MonitorSmartphone, TrendingDown, Plug, Clock, GraduationCap, LifeBuoy, AlertTriangle, Gavel, ListChecks, History, Trash2, Target } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { usePermissions } from "../context/PermissionsContext";
import { api } from "../lib/api";

const FAV_KEY_PREFIX = "jms.favorites.v1";

// AP-106 · Badge com contador de documentos aguardando análise no menu Central
function useCentralQueueCount(canFn) {
  const [count, setCount] = useState(0);
  const canView = canFn("central_documents", "view");
  useEffect(() => {
    if (!canView) { setCount(0); return; }
    let alive = true;
    const load = () => api.get("/central-documents/requirements/stats")
      .then(({ data }) => { if (alive) setCount(data?.awaiting_analysis || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [canView]);
  return count;
}

export default function Sidebar({ open = false, onClose }) {
  const { t } = useI18n();
  const { logout, user } = useAuth();
  const { settings } = useSettings();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const centralQueueCount = useCentralQueueCount(can);

  // AP-098 · Dynamic RBAC Navigation. NO hardcoded role checks. Every item
  // requires a permission tuple `[module, action]` and is filtered via can().
  // Groups (Operações/Documentos/Financeiro/Equipe/Gestão/Sistema) auto-hide
  // when empty — no `isMSA`, no `role === "…"` checks anywhere.
  const allItems = [
    { to: "/dashboard", label: t("nav_dashboard"), icon: LayoutDashboard, testid: "nav-dashboard", perm: ["dashboard", "view"], section: "ops" },
    { to: "/customers", label: t("nav_customers"), icon: Users, testid: "nav-customers", perm: ["customers", "view"], section: "ops" },
    // AP-154 · Lixeira de Clientes (Master-only via role guard)
    { to: "/customers/trash", label: "Lixeira", icon: Trash2, testid: "nav-customers-trash", perm: ["customers", "view"], roles: ["master_super_admin"], section: "ops" },
    { to: "/processes", label: "Processos", icon: FolderKanban, testid: "nav-processes", perm: ["processes", "view"], section: "ops" },
    { to: "/services",  label: t("nav_services"),  icon: Briefcase, testid: "nav-services", perm: ["services", "view"], section: "ops" },
    { to: "/cadastros/servicos-administrativos", label: "Serviços Administrativos", icon: Layers, testid: "nav-admin-services", perm: ["services", "view"], section: "ops" },
    { to: "/appointments", label: t("nav_appointments"), icon: CalendarClock, testid: "nav-appointments", perm: ["appointments", "view"], section: "ops" },

    { to: "/documents", label: t("nav_documents"), icon: FileText, testid: "nav-documents", perm: ["documents", "view"], section: "docs" },
    { to: "/central-documents", label: "Central de Documentos", icon: Archive, testid: "nav-central-documents", perm: ["central_documents", "view"], section: "docs", badge: centralQueueCount > 0 ? centralQueueCount : null },
    // AP-154 · Lixeira de Documentos (Master-only)
    { to: "/central-documents/trash", label: "Lixeira de Documentos", icon: Trash2, testid: "nav-docs-trash", perm: ["central_documents", "view"], roles: ["master_super_admin"], section: "docs" },

    { to: "/payments",  label: t("nav_payments"),  icon: CreditCard, testid: "nav-payments", perm: ["payments", "view"], section: "finance" },
    { to: "/finance/trash", label: "Lixeira Financeira", icon: Trash2, testid: "nav-finance-trash", perm: ["payments", "view"], roles: ["master_super_admin"], section: "finance" },
    { to: "/payments/verification-queue", label: "Fila de Verificação", icon: Clock, testid: "nav-verification-queue", perm: ["payments", "view"], roles: ["master_super_admin", "pdg", "secretary"], section: "finance" },
    { to: "/expenses",  label: "Despesas",         icon: TrendingDown, testid: "nav-expenses", perm: ["payments", "view"], section: "finance" },
    { to: "/balance",   label: t("nav_balance"),   icon: Wallet, testid: "nav-balance", perm: ["balance", "view"], section: "finance" },
    { to: "/partner-settlements", label: "Repasses a Parceiros", icon: Handshake, testid: "nav-partner-settlements", perm: ["balance", "view"], section: "finance" },

    // AP-160 · Módulo Cobranças
    { to: "/collections", label: "Painel de Cobranças", icon: AlertTriangle, testid: "nav-collections-dashboard", perm: ["collections", "view"], section: "collections" },
    { to: "/collections/pending", label: "Pendências", icon: ListChecks, testid: "nav-collections-pending", perm: ["collections", "view"], section: "collections" },
    { to: "/collections/negotiations", label: "Em Negociação", icon: MessageSquare, testid: "nav-collections-negotiations", perm: ["collections", "view"], section: "collections" },
    { to: "/collections/agreements", label: "Acordos", icon: Gavel, testid: "nav-collections-agreements", perm: ["collections", "view"], section: "collections" },
    { to: "/collections/history", label: "Histórico", icon: History, testid: "nav-collections-history", perm: ["collections", "view"], section: "collections" },


    { to: "/communication", label: "Comunicação Interna", icon: MessageSquare, testid: "nav-communication", perm: ["communication", "view"], section: "team" },
    { to: "/support-tickets", label: "Tickets de Suporte", icon: LifeBuoy, testid: "nav-support-tickets", perm: ["support_tickets", "view"], section: "team" },
    { to: "/internal-reports", label: "Relatórios Internos", icon: ClipboardCheck, testid: "nav-internal-reports", perm: ["reports", "view"], section: "team" },
    { to: "/tech-admin", label: "JMS TECH", icon: GraduationCap, testid: "nav-jms-tech", perm: ["tech_dashboard", "view"], section: "mgmt" },
    // AP-020 · Metas & KPI
    { to: "/metas-kpi/dashboard", label: "Metas & KPI · Dashboard", icon: Target, testid: "nav-kpi-dashboard", perm: ["goals_kpi", "view"], section: "kpi" },
    { to: "/metas-kpi/mensais", label: "Metas Mensais", icon: Target, testid: "nav-kpi-mensais", perm: ["goals_kpi", "view"], section: "kpi" },
    { to: "/metas-kpi/semanais", label: "Metas Semanais", icon: CalendarClock, testid: "nav-kpi-semanais", perm: ["goals_kpi", "view"], section: "kpi" },
    { to: "/metas-kpi/setores", label: "KPI por Setor", icon: Layers, testid: "nav-kpi-setores", perm: ["goals_kpi", "view"], section: "kpi" },
    { to: "/metas-kpi/funcionarios", label: "KPI por Funcionário", icon: UsersRound, testid: "nav-kpi-funcionarios", perm: ["goals_kpi", "view"], section: "kpi" },
    { to: "/metas-kpi/resultados", label: "Resultados", icon: ListChecks, testid: "nav-kpi-resultados", perm: ["goals_kpi", "view"], section: "kpi" },
    { to: "/metas-kpi/historico", label: "Histórico", icon: History, testid: "nav-kpi-historico", perm: ["goals_kpi", "audit"], section: "kpi" },

    { to: "/notifications", label: "Central de Notificações", icon: Bell, testid: "nav-notifications", perm: ["audit", "view"], section: "mgmt" },
    { to: "/monitoring", label: "Centro de Monitoramento", icon: MonitorSmartphone, testid: "nav-monitoring", perm: ["audit", "view"], section: "mgmt" },

    { to: "/hr/employees", label: "Funcionários", icon: UserCog, testid: "nav-hr-employees", perm: ["employees", "view"], section: "team" },
    { to: "/hr/employee-requests", label: "Solicitações", icon: ClipboardCheck, testid: "nav-hr-requests", perm: ["hr", "view"], section: "team" },
    { to: "/payroll/dashboard", label: "Folha de Pagamento", icon: Banknote, testid: "nav-payroll", perm: ["payroll", "view"], section: "team" },

    { to: "/users",     label: "Usuários",          icon: UsersRound, testid: "nav-users", perm: ["users", "view"], section: "system" },
    { to: "/settings/roles", label: "Papéis e Permissões", icon: ShieldCheck, testid: "nav-roles", perm: ["roles", "view"], section: "system" },
    { to: "/settings/audit", label: "Central de Auditoria", icon: ScrollText, testid: "nav-audit", perm: ["audit", "view"], section: "mgmt" },
    // AP-098 · Substituídos os hardcoded role-checks (isMSA / includes([...]))
    // por permissões RBAC criadas exclusivamente para este fim.
    { to: "/settings/activity-center", label: "Activity Center", icon: Activity, testid: "nav-activity-center", perm: ["activity_center", "view"], section: "mgmt" },
    { to: "/settings/system-tools", label: "Ferramentas de Sistema", icon: Wrench, testid: "nav-system-tools", perm: ["system_tools", "view"], section: "system" },
    { to: "/settings/integrations", label: "Integrações", icon: Plug, testid: "nav-integrations", perm: ["integrations", "view"], section: "system" },
    // AP-103 FINAL · Portal do Cliente
    { to: "/settings/portal-client", label: "Portal do Cliente", icon: Users, testid: "nav-portal-client", perm: ["settings", "view"], section: "system" },
    // AP-101 · Multi-Tenant Empresas (Super Admin only, gated pelo bypass Master)
    { to: "/super/companies", label: "Empresas", icon: Briefcase, testid: "nav-super-companies", perm: ["system_tools", "view"], section: "system" },
    { to: "/admin/duplicate-clients", label: "Duplicados de Clientes", icon: Copy, testid: "nav-duplicate-clients", perm: ["system_tools", "view"], section: "system" },
    { to: "/settings",  label: t("nav_settings"),  icon: SettingsIcon, testid: "nav-settings", perm: ["settings", "view"], section: "system" },
  ];
  const items = allItems.filter((i) => {
    if (!can(i.perm[0], i.perm[1])) return false;
    // AP-096 V2 · role gate opcional (para itens que só um subset de roles vê)
    if (Array.isArray(i.roles) && i.roles.length > 0 && !i.roles.includes(user?.role)) return false;
    return true;
  });

  // AP-078 · Reorganização do menu lateral em 6 grupos (Operações, Documentos,
  // Financeiro, Equipe, Gestão, Sistema). Apenas UI — nenhuma rota, testid,
  // ícone, permissão ou lógica de negócio foi alterada.
  const SECTIONS = [
    { key: "ops",     label: "Operações" },
    { key: "docs",    label: "Documentos" },
    { key: "finance", label: "Financeiro" },
    { key: "collections", label: "Cobranças" },
    { key: "team",    label: "Equipe" },
    { key: "mgmt",    label: "Gestão" },
    { key: "kpi",     label: "Metas & KPI" },
    { key: "system",  label: "Sistema" },
  ];
  const itemsBySection = useMemo(() => {
    const map = {};
    for (const s of SECTIONS) map[s.key] = [];
    for (const it of items) (map[it.section] || map.system).push(it);
    return map;
  }, [items]);

  // Initials for user avatar
  const initials = useMemo(() => {
    const src = (user?.full_name || user?.name || user?.email || "?").trim();
    const parts = src.split(/[\s._@]+/).filter(Boolean);
    return (parts[0]?.[0] || "?").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
  }, [user]);

  // AP-021 Sprint 2 · Favorites (drag & drop, per-user localStorage)
  const favKey = `${FAV_KEY_PREFIX}.${user?.id || "anon"}`;
  const [favorites, setFavorites] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(favKey);
      setFavorites(raw ? JSON.parse(raw) : []);
    } catch { setFavorites([]); }
    // Listen for external changes (from CommandPalette)
    const onChange = (e) => setFavorites(e.detail || []);
    window.addEventListener("jms:favorites-changed", onChange);
    return () => window.removeEventListener("jms:favorites-changed", onChange);
  }, [favKey]);

  const persistFav = (next) => {
    setFavorites(next);
    try { localStorage.setItem(favKey, JSON.stringify(next)); } catch {}
    window.dispatchEvent(new CustomEvent("jms:favorites-changed", { detail: next }));
  };

  const favItems = useMemo(
    () => favorites
      .map((p) => items.find((i) => i.to === p))
      .filter(Boolean),
    [favorites, items]
  );

  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    try { e.dataTransfer.effectAllowed = "move"; } catch {}
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    setDropIdx(idx);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx == null || dragIdx === idx) { setDragIdx(null); setDropIdx(null); return; }
    const next = [...favorites];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    persistFav(next);
    setDragIdx(null); setDropIdx(null);
  };
  const removeFavorite = (path) => persistFav(favorites.filter((p) => p !== path));

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <>
      {/* AP-056 · Mobile overlay backdrop (below lg breakpoint). */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        data-testid="app-sidebar"
        className={
          // AP-022 · Fixed sidebar (never scrolls with page content).
          //   ≥ lg → fixed to viewport (h-screen, top-0, left-0)
          //   < lg → off-canvas drawer with translate
          "flex flex-col w-64 border-r border-slate-800 bg-slate-900 text-slate-300 h-screen " +
          "fixed top-0 left-0 z-50 " +
          "transform transition-transform duration-200 ease-in-out lg:transform-none " +
          (open ? "translate-x-0 " : "-translate-x-full ") +
          "lg:translate-x-0"
        }
        onClick={(e) => {
          // Auto-close the drawer when the user picks a nav item on mobile.
          if (window.innerWidth < 1024) {
            const link = e.target.closest("a[href]");
            if (link && onClose) onClose();
          }
        }}
      >
      <div className="px-6 py-5 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-primary to-emerald-600 grid place-items-center shadow-[0_8px_20px_-6px_rgba(22,163,74,0.7)]">
            <Wallet className="h-5 w-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <p className="font-display font-bold text-base tracking-tight text-white truncate">
              {settings?.company_name || "JMS ERP"}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Business Suite</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto jms-scroll-dark">
        {favItems.length > 0 && (
          <div className="mb-3 pb-3 border-b border-slate-800" data-testid="sidebar-favorites">
            <div className="flex items-center gap-2 px-3 mb-2">
              <Star className="h-3.5 w-3.5 text-primary" fill="currentColor" />
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Favoritos</span>
              <span className="text-[10px] text-slate-600 ml-auto">arraste p/ ordenar</span>
            </div>
            <ul className="space-y-1">
              {favItems.map((it, idx) => (
                <li
                  key={it.to}
                  draggable
                  onDragStart={onDragStart(idx)}
                  onDragOver={onDragOver(idx)}
                  onDrop={onDrop(idx)}
                  onDragEnd={() => { setDragIdx(null); setDropIdx(null); }}
                  data-testid={`fav-item-${it.to.replace(/\//g, "-")}`}
                  className={`group flex items-center gap-1 rounded-md pr-1 transition-all ${
                    dropIdx === idx && dragIdx !== null && dragIdx !== idx ? "ring-2 ring-primary/40" : ""
                  } ${dragIdx === idx ? "opacity-40" : ""}`}
                >
                  <GripVertical
                    data-testid={`fav-grip-${it.to.replace(/\//g, "-")}`}
                    className="h-3.5 w-3.5 text-muted-foreground/40 cursor-grab active:cursor-grabbing group-hover:text-muted-foreground/70"
                  />
                  <NavLink
                    to={it.to}
                    end={it.to === "/dashboard"}
                    className={({ isActive }) =>
                      `flex-1 flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-medium ${
                        isActive ? "text-primary bg-primary/10" : "text-foreground/80 hover:bg-muted"
                      }`
                    }
                  >
                    <it.icon className="h-3.5 w-3.5" />
                    <span className="truncate">{it.label}</span>
                  </NavLink>
                  <button
                    type="button"
                    onClick={() => removeFavorite(it.to)}
                    aria-label="Remover dos favoritos"
                    data-testid={`fav-remove-${it.to.replace(/\//g, "-")}`}
                    className="text-primary/70 opacity-0 group-hover:opacity-100 hover:text-primary transition-opacity p-0.5"
                  >
                    <Star className="h-3 w-3" fill="currentColor" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {items.length > 0 && SECTIONS.map(({ key, label }) => {
          const secItems = itemsBySection[key] || [];
          if (secItems.length === 0) return null;
          return (
            <div key={key} className="mb-5" data-testid={`sidebar-section-${key}`}>
              <div className="px-3 mb-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                {label}
              </div>
              {secItems.map(({ to, label, icon: Icon, testid, badge }) => {
                const isFav = favorites.includes(to);
                return (
                  <div key={to} className="group relative">
                    <NavLink
                      to={to}
                      data-testid={testid}
                      end={to === "/dashboard"}
                      className={({ isActive }) =>
                        `nav-item flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                          isActive
                            ? "text-white bg-primary/20 shadow-[inset_2px_0_0_hsl(var(--primary)),0_4px_14px_-6px_rgba(22,163,74,0.5)]"
                            : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                        }`
                      }
                    >
                      <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                      <span className="flex-1 truncate">{label}</span>
                      {badge != null && badge > 0 && (
                        <span
                          data-testid={`${testid}-badge`}
                          className="inline-flex items-center justify-center min-w-[18px] h-4 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold tabular-nums"
                        >
                          {badge > 99 ? "99+" : badge}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation();
                          persistFav(isFav ? favorites.filter((p) => p !== to) : [...favorites, to]);
                        }}
                        aria-label={isFav ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                        data-testid={`nav-fav-toggle-${testid}`}
                        className={`p-0.5 rounded transition-opacity ${isFav ? "text-primary opacity-100" : "text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:text-primary"}`}
                      >
                        <Star className="h-3 w-3" fill={isFav ? "currentColor" : "none"} />
                      </button>
                    </NavLink>
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="px-4 py-4 border-t border-slate-800 bg-slate-950/60">
        <div className="flex items-center gap-3">
          <div
            data-testid="sidebar-user-avatar"
            className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-br from-primary to-emerald-600 grid place-items-center text-white text-xs font-bold shadow-[0_6px_16px_-4px_rgba(22,163,74,0.6)] ring-2 ring-slate-800"
            aria-hidden="true"
          >
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p data-testid="sidebar-user-name" className="text-sm font-semibold text-white truncate">{user?.full_name || user?.name || "Admin"}</p>
            <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="p-2 rounded-lg hover:bg-slate-800 transition-all text-slate-400 hover:text-red-400 shrink-0"
            title={t("logout")}
            aria-label={t("logout")}
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
    </>
  );
}
